import React, { useEffect } from 'react';
import { string, number } from 'prop-types';
import classNames from 'classnames/bind';
import {
  params,
  shape,
  fragment,
  customType,
  string as stringType,
  number as numberType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { Image, Button } from '@thd-olt-component-react/core-ui';
import styles from './story-hero.module.scss';

const cx = classNames.bind(styles);

const StoryHero = ({ componentId, componentClass, componentPosition }) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('contentfulhero.ready');
  }, []);

  const { data, loading, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
    skip: !(componentId && componentClass),
  });

  const marketingData = data?.component?.marketingData;
  const {
    eyebrow: eyebrowProductData,
    title: titleProductData,
    description: descriptionProductData,
    link: linkProductData,
    cta: ctaProductData,
  } = data?.component || {};

  const {
    url: urlProductData,
    details: detailsProductData,
    id: idProductData,
    contentType: contentTypeProductData,
  } = data?.component?.image || {};

  let eyebrow = eyebrowProductData;
  let title = titleProductData;
  let description = descriptionProductData;
  let link = linkProductData;
  let cta = ctaProductData;
  let url = urlProductData;
  let details = detailsProductData;
  let id = idProductData;
  let contentType = contentTypeProductData;

  if (marketingData) {
    eyebrow = marketingData.eyebrow_LessThan30Char;
    description = marketingData.description;
    link = marketingData.link;
    cta = marketingData.cta;
    title = marketingData.title_LessThan50Char;
    url = marketingData.lifestyleImage?.damDownloadedContent?.url;
    details = marketingData.lifestyleImage?.damDownloadedContent?.details;
    id = marketingData.lifestyleImage?.damDownloadedContent?.id;
  }

  return (
    <div
      className={cx('story-hero__card-wrapper')}
      id={`${componentClass}-${componentPosition}-${componentId}`}
      data-component-id={componentId}
      data-component-name={componentClass}
      data-component-position={componentPosition}
    >
      <div className={cx('story-hero__card')}>
        <div className={cx('story-hero__image-container')}>
          {url && (
            <Image
              stretchy
              src={url}
              alt={`Image for ${title}`}
              title={`Image for ${title}`}
              height={details.height}
              width={details.width}
              className={cx('story-hero__image')}
              fetchpriority="high"
            />
          )}
        </div>
        <div className={cx('story-hero__info-box')}>
          <div className={cx('story-hero__text-box')}>
            <div className={cx('story-hero__eyebrow-box')}>
              {eyebrow && <p className={cx('story-hero__eyebrow')}>{eyebrow}</p>}
            </div>
            <h2 className={cx('story-hero__title')}>{title}</h2>
            <p className={cx('story-hero__text')}>{description}</p>
          </div>
          {link && cta && (
            <a href={link}>
              <Button className={cx('story-hero__info-box__button')} inline>{cta}</Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

StoryHero.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  componentId: string.isRequired,
  componentClass: string.isRequired,
  componentPosition: number
};

StoryHero.defaultProps = {
  componentPosition: 1
};

StoryHero.displayName = 'Contentful Story Hero';

StoryHero.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['Hero']).isRequired(),
  }).shape({
    Hero: fragment().shape({
      cta: stringType(),
      description: stringType(),
      eyebrow: stringType(),
      id: stringType(),
      image: shape({
        contentType: stringType(),
        description: stringType(),
        details: shape({
          height: numberType(),
          size: numberType(),
          width: numberType(),
        }),
        id: stringType(),
        title: stringType(),
        url: stringType(),
      }),
      marketingData: shape({
        link: stringType(),
        lifestyleImage: shape({
          damDownloadedContent: shape({
            id: stringType(),
            url: stringType(),
            contentType: stringType(),
            title: stringType(),
            description: stringType(),
            details: shape({
              height: numberType(),
              width: numberType(),
              size: numberType(),
            }),
          }),
        }),
        silohImage: shape({
          damDownloadedContent: shape({
            id: stringType(),
            url: stringType(),
            contentType: stringType(),
            title: stringType(),
            description: stringType(),
            details: shape({
              height: numberType(),
              width: numberType(),
              size: numberType(),
            }),
          }),
        }),
        title_LessThan50Char: stringType(),
        title_LessThan40Char: stringType(),
        description: stringType(),
        cta: stringType(),
        eyebrow_LessThan30Char: stringType(),
        badge: shape({
          damDownloadedContent: shape({
            id: stringType(),
            url: stringType(),
            contentType: stringType(),
            title: stringType(),
            description: stringType(),
            details: shape({
              height: numberType(),
              width: numberType(),
              size: numberType(),
            }),
          }),
        }),
        legal: stringType(),
      }),
      link: stringType(),
      title: stringType(),
    }),
  }),
};

export { StoryHero };
